import React from 'react';
import {
  GRID_COLUMNS,
  GRID_PAGE_SIZE,
  INVENTORY_PAGE_COUNT,
  ItemImage,
  buildSpanGridStyle,
  computePageCoverage,
  isSlotLocked
} from './inventoryBagGridUtils';

const InventoryBagGrid = ({
  gridSlots,
  currentInventoryPage,
  onPageChange,
  unlockedSlotsCount,
  dragOverIndex = null,
  isDropBlocked,
  draggable = true,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onTouchStart,
  onClick,
  slotKeyPrefix = 'inventory',
  wrapperClassName = '',
  gridAreaClassName = '',
  gridClassName = 'inventory-grid-10x10',
  areaRef = null,
  emptySlotTitle = '',
  lockedSlotTitle,
  slotExtraClassName = '',
  onPageTabDragOver,
  onPageTabTouchMove,
  onPageTabTouchEnd,
  pageTabsClassName = 'inventory-page-tabs',
  isTouchDragging = false,
  renderSlotContent
}) => {
  const resolveDropBlocked = (absoluteIndex) => (
    typeof isDropBlocked === 'function' ? isDropBlocked(absoluteIndex) : false
  );

  return (
    <div className={`inventory-grid-wrapper ${wrapperClassName}`.trim()}>
      <div className={`inventory-grid-area ${gridAreaClassName}`.trim()} ref={areaRef}>
        <div className="inventory-pages-stack">
          {Array.from({ length: INVENTORY_PAGE_COUNT }, (_, pageIndex) => {
            const page = pageIndex + 1;
            const pageStartIndex = pageIndex * GRID_PAGE_SIZE;
            const pageSlots = gridSlots.slice(pageStartIndex, pageStartIndex + GRID_PAGE_SIZE);
            const { coveredIndexes, spanByIndex } = computePageCoverage(pageSlots, pageStartIndex);
            return (
              <div
                key={`${slotKeyPrefix}-page-grid-${page}`}
                className={`${gridClassName} inventory-grid-page ${currentInventoryPage === page ? 'active' : 'hidden'}`}
              >
                {pageSlots.map((slotItem, index) => {
                  const absoluteIndex = pageStartIndex + index;
                  const colStart = (index % GRID_COLUMNS) + 1;
                  const rowStart = Math.floor(index / GRID_COLUMNS) + 1;
                  const span = spanByIndex[absoluteIndex];
                  const isHoverTarget = dragOverIndex === absoluteIndex;
                  const isBlockedTarget = isHoverTarget && resolveDropBlocked(absoluteIndex);

                  if (coveredIndexes.has(absoluteIndex)) {
                    return null;
                  }

                  if (isSlotLocked(absoluteIndex, unlockedSlotsCount)) {
                    return (
                      <div
                        key={`${slotKeyPrefix}-slot-locked-${absoluteIndex}`}
                        className="inventory-slot-box inventory-slot-box--locked empty"
                        style={{
                          gridColumnStart: colStart,
                          gridRowStart: rowStart
                        }}
                        title={lockedSlotTitle || emptySlotTitle}
                      />
                    );
                  }

                  const canDrag = typeof draggable === 'function'
                    ? draggable(slotItem, absoluteIndex)
                    : draggable && !!slotItem;

                  return (
                    <div
                      key={`${slotKeyPrefix}-slot-${absoluteIndex}`}
                      className={`inventory-slot-box ${slotExtraClassName} ${span ? 'multi-size' : ''} ${slotItem ? 'filled' : 'empty'} ${isHoverTarget ? (isBlockedTarget ? 'drop-target-invalid' : 'drop-target') : ''}`.trim()}
                      data-slot-index={absoluteIndex}
                      style={{
                        gridColumnStart: colStart,
                        gridRowStart: rowStart,
                        ...(span ? buildSpanGridStyle(span) : {})
                      }}
                      draggable={canDrag}
                      onDragStart={(event) => onDragStart?.(event, absoluteIndex, slotItem)}
                      onDragEnter={(event) => onDragOver?.(event, absoluteIndex)}
                      onDragOver={(event) => onDragOver?.(event, absoluteIndex)}
                      onDragLeave={() => onDragLeave?.(absoluteIndex)}
                      onDrop={(event) => onDrop?.(event, absoluteIndex)}
                      onDragEnd={() => onDragEnd?.()}
                      onTouchStart={(event) => onTouchStart?.(event, absoluteIndex, slotItem)}
                      onClick={(event) => {
                        if (isTouchDragging) return;
                        onClick?.(event, slotItem, absoluteIndex);
                      }}
                      title={slotItem ? `${slotItem.baseItem.name} (${slotItem.categoryLabel})` : emptySlotTitle}
                    >
                      {renderSlotContent ? renderSlotContent(slotItem) : (
                        slotItem ? (
                          <>
                            <div className="slot-item-image">
                              {slotItem.baseItem.imageUrl ? (
                                <ItemImage
                                  imageUrl={slotItem.baseItem.imageUrl}
                                  alt={slotItem.baseItem.name}
                                  singleFrameSprite={
                                    slotItem.inventoryType === 'boots' ||
                                    slotItem.slotType === 'boot'
                                  }
                                />
                              ) : (
                                <div className="slot-placeholder-icon">📦</div>
                              )}
                            </div>
                            {slotItem.count > 1 && (
                              <span className="slot-item-count">x{slotItem.count}</span>
                            )}
                          </>
                        ) : null
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className={pageTabsClassName}>
          {Array.from({ length: INVENTORY_PAGE_COUNT }, (_, index) => (
            <button
              key={`${slotKeyPrefix}-page-${index + 1}`}
              type="button"
              className={`inventory-page-tab ${currentInventoryPage === index + 1 ? 'active' : ''}`}
              data-inventory-page={index + 1}
              onClick={() => onPageChange(index + 1)}
              onDragOver={onPageTabDragOver ? (event) => onPageTabDragOver(event, index + 1) : undefined}
              onDrop={(event) => event.preventDefault()}
              onTouchMove={onPageTabTouchMove}
              onTouchEnd={onPageTabTouchEnd}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InventoryBagGrid;
